import type { EndpointDef } from '@skynet/data-helpers';

/**
 * Central endpoint definitions — single source of truth for the Exchanges API.
 *
 * This file drives:
 * - **Enhanced API**: computed endpoint keys in `enhanceEndpoints`
 * - **Hook re-exports**: clean name → generated name mapping
 * - **MSW mock factories**: HTTP method + URL path pattern
 *
 * When the OpenAPI spec changes and codegen regenerates, update this
 * file to keep everything in sync.
 */
export const EXCHANGES_API_ENDPOINTS = {
  // Takes `{ ipScope?: ExchangeIpScope }` since SKY-4604, so call it with `{}`, not with no argument.
  // Pass `ipScope` wherever the resolved IP strings matter: the spec and the backend disagree on its default.
  listExchanges: {
    generated: 'exchangesAdminControllerListExchangesV1',
    method: 'get',
    path: '/api/v1/admin/exchanges',
  },
  getExchange: {
    generated: 'exchangesAdminControllerGetExchangeV1',
    method: 'get',
    path: '/api/v1/admin/exchanges/:id',
  },
  createExchange: {
    generated: 'exchangesAdminControllerCreateExchangeV1',
    method: 'post',
    path: '/api/v1/admin/exchanges',
  },
  updateExchange: {
    generated: 'exchangesAdminControllerUpdateExchangeV1',
    method: 'put',
    path: '/api/v1/admin/exchanges/:id',
  },
  deleteExchange: {
    generated: 'exchangesAdminControllerDeleteExchangeV1',
    method: 'delete',
    path: '/api/v1/admin/exchanges/:id',
  },
  syncExchange: {
    generated: 'exchangesAdminSyncControllerSyncExchangeV1',
    method: 'post',
    path: '/api/v1/admin/exchanges/:id/sync',
  },
  getExchangeChangelogs: {
    generated: 'exchangesAdminSyncControllerGetExchangeChangelogsV1',
    method: 'get',
    path: '/api/v1/admin/exchanges/:id/changelogs',
  },
  getExchangeSyncLogs: {
    generated: 'exchangesAdminSyncControllerGetExchangeSyncLogsV1',
    method: 'get',
    path: '/api/v1/admin/exchanges/:id/synclogs',
  },
  // Caller-scoped ("public") exchange list. Its `GET /api/v1/exchanges` response
  // has no schema in the OpenAPI spec (only examples), so codegen emits an
  // untyped `Blob` result — the response is re-typed to `PublicExchange[]` via
  // `OverrideResultType` in `exchanges-api.ts`. The `scope` query param is still
  // typed by the spec.
  listPublicExchanges: {
    generated: 'exchangesControllerListExchangesRootV1',
    method: 'get',
    path: '/api/v1/exchanges',
  },
  // SKY-4604 (endpoints from SKY-4571): an exchange's own IP addresses, one row per address per transport.
  listExchangeIps: {
    generated: 'exchangeIpsAdminControllerListExchangeIpsV1',
    method: 'get',
    path: '/api/v1/admin/exchanges/:id/ips',
  },
  createExchangeIp: {
    generated: 'exchangeIpsAdminControllerCreateExchangeIpV1',
    method: 'post',
    path: '/api/v1/admin/exchanges/:id/ips',
  },
  updateExchangeIp: {
    generated: 'exchangeIpsAdminControllerUpdateExchangeIpV1',
    method: 'put',
    path: '/api/v1/admin/exchanges/:id/ips/:ipId',
  },
  deleteExchangeIp: {
    generated: 'exchangeIpsAdminControllerDeleteExchangeIpV1',
    method: 'delete',
    path: '/api/v1/admin/exchanges/:id/ips/:ipId',
  },
  // SKY-4604 (endpoints from SKY-4571): regions an exchange inherits IPs from, one link per transport.
  listExchangeRegions: {
    generated: 'exchangeRegionsAdminControllerListExchangeRegionsV1',
    method: 'get',
    path: '/api/v1/admin/exchanges/:id/regions',
  },
  createExchangeRegion: {
    generated: 'exchangeRegionsAdminControllerCreateExchangeRegionV1',
    method: 'post',
    path: '/api/v1/admin/exchanges/:id/regions',
  },
  deleteExchangeRegion: {
    generated: 'exchangeRegionsAdminControllerDeleteExchangeRegionV1',
    method: 'delete',
    path: '/api/v1/admin/exchanges/:id/regions/:linkId',
  },
} as const satisfies Record<string, EndpointDef>;

export type ExchangesEndpointName = keyof typeof EXCHANGES_API_ENDPOINTS;
